/**
 * Teams Authentication Provider.
 *
 * Unified auth provider that:
 * - Detects Teams environment on mount
 * - Uses Teams SSO when in Teams
 * - Falls back to MSAL when in browser
 * - Provides consistent auth context to app
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import {
  isInTeams,
  getTeamsToken,
  getTeamsContext,
  notifyTeamsAppLoaded,
} from "@/lib/teams-auth";
import { loginRequest } from "@/lib/msal-config";

/**
 * Decode JWT token payload to extract user claims.
 * Teams SSO tokens contain: name, preferred_username, oid, etc.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    // Base64url decode
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.warn("[TeamsAuth] Failed to decode JWT:", error);
    return null;
  }
}

interface TeamsAuthContextType {
  /** Whether the app is running inside Microsoft Teams */
  isTeams: boolean;
  /** Whether the user is authenticated (via Teams or MSAL) */
  isAuthenticated: boolean;
  /** Whether auth is still loading/checking */
  isLoading: boolean;
  /** The current access token (Teams SSO or MSAL) */
  accessToken: string | null;
  /** User information */
  user: {
    name?: string;
    email?: string;
    id?: string;
  } | null;
  /** Trigger login (only needed outside Teams) */
  login: () => Promise<void>;
  /** Trigger logout */
  logout: () => Promise<void>;
  /** Get a fresh access token */
  getAccessToken: () => Promise<string | null>;
}

const TeamsAuthContext = createContext<TeamsAuthContextType | null>(null);

interface TeamsAuthProviderProps {
  children: ReactNode;
}

export function TeamsAuthProvider({ children }: TeamsAuthProviderProps) {
  const { instance, accounts, inProgress } = useMsal();
  const msalAuthenticated = useIsAuthenticated();

  const [isTeams, setIsTeams] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [teamsToken, setTeamsToken] = useState<string | null>(null);
  const [teamsUser, setTeamsUser] = useState<{
    name?: string;
    email?: string;
    id?: string;
  } | null>(null);

  // Check if running in Teams on mount
  useEffect(() => {
    let cancelled = false;

    async function detectTeams() {
      console.log("[TeamsAuth] Starting Teams detection...");
      try {
        const inTeams = await isInTeams();
        console.log("[TeamsAuth] isInTeams result:", inTeams);

        if (cancelled) return;

        if (inTeams) {
          setIsTeams(true);
          console.log("[TeamsAuth] In Teams - getting SSO token...");

          // Get Teams SSO token
          const token = await getTeamsToken();
          console.log("[TeamsAuth] Token acquired:", !!token);

          let userFromToken: {
            name?: string;
            email?: string;
            id?: string;
          } | null = null;

          if (token && !cancelled) {
            setTeamsToken(token);

            // Decode JWT to get user info from token claims
            // This is more reliable than Teams context which often has empty displayName
            const claims = decodeJwtPayload(token);
            console.log("[TeamsAuth] Token claims:", claims);

            if (claims) {
              userFromToken = {
                // 'name' claim contains the display name
                name:
                  (claims.name as string) ||
                  (claims.given_name as string) ||
                  (claims.preferred_username as string)?.split("@")[0],
                // 'preferred_username' or 'upn' contains the email
                email:
                  (claims.preferred_username as string) ||
                  (claims.upn as string) ||
                  (claims.email as string),
                // 'oid' is the Azure AD object ID
                id: claims.oid as string,
              };
              console.log("[TeamsAuth] User from token:", userFromToken);
            }
          }

          // Get Teams context for user info (fallback)
          const context = await getTeamsContext();
          console.log("[TeamsAuth] Context user:", context?.user);

          if (!cancelled) {
            // Prefer token claims, fallback to context
            setTeamsUser({
              name:
                userFromToken?.name ||
                context?.user?.displayName ||
                context?.user?.userPrincipalName?.split("@")[0],
              email: userFromToken?.email || context?.user?.userPrincipalName,
              id: userFromToken?.id || context?.user?.id,
            });
          }

          // Notify Teams that app is loaded
          await notifyTeamsAppLoaded();
          console.log("[TeamsAuth] Teams app loaded notification sent");
        } else {
          console.log("[TeamsAuth] Not in Teams - using MSAL");
        }
      } catch (error) {
        console.info("[TeamsAuth] Not in Teams environment:", error);
      } finally {
        if (!cancelled) {
          console.log(
            "[TeamsAuth] Detection complete, setting isLoading to false",
          );
          setIsLoading(false);
        }
      }
    }

    detectTeams();

    return () => {
      cancelled = true;
    };
  }, []);

  // Determine authentication state
  const isAuthenticated = isTeams ? !!teamsToken : msalAuthenticated;
  console.log("[TeamsAuth] Auth state:", {
    isTeams,
    teamsToken: !!teamsToken,
    msalAuthenticated,
    isAuthenticated,
    isLoading,
    teamsUser,
    accounts,
  });

  // Get current user info
  const user = isTeams
    ? teamsUser
    : accounts[0]
      ? {
          name: accounts[0].name,
          email: accounts[0].username,
          id: accounts[0].localAccountId,
        }
      : null;

  // Get access token (unified for Teams and MSAL)
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (isTeams) {
      // Refresh Teams token if needed
      const token = await getTeamsToken();
      if (token) {
        setTeamsToken(token);
      }
      return token;
    }

    // Use MSAL
    if (accounts.length > 0) {
      try {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        return response.accessToken;
      } catch (error) {
        console.error("[TeamsAuth] Failed to acquire MSAL token:", error);
        return null;
      }
    }

    return null;
  }, [isTeams, instance, accounts]);

  // Login function (only for browser, Teams uses SSO)
  const login = useCallback(async () => {
    if (isTeams) {
      // In Teams, try to get token again
      const token = await getTeamsToken();
      if (token) {
        setTeamsToken(token);
      }
      return;
    }

    // Use MSAL popup login
    try {
      await instance.loginPopup(loginRequest);
    } catch (error) {
      console.error("[TeamsAuth] Login failed:", error);
      throw error;
    }
  }, [isTeams, instance]);

  // Logout function
  const logout = useCallback(async () => {
    if (isTeams) {
      // Can't logout from Teams SSO - just clear local state
      setTeamsToken(null);
      setTeamsUser(null);
      return;
    }

    // MSAL logout
    try {
      await instance.logoutPopup();
    } catch (error) {
      console.error("[TeamsAuth] Logout failed:", error);
      throw error;
    }
  }, [isTeams, instance]);

  // Show loading while checking Teams or MSAL init
  const authLoading = isLoading || inProgress !== InteractionStatus.None;

  const value: TeamsAuthContextType = {
    isTeams,
    isAuthenticated,
    isLoading: authLoading,
    accessToken: isTeams ? teamsToken : null,
    user,
    login,
    logout,
    getAccessToken,
  };

  return (
    <TeamsAuthContext.Provider value={value}>
      {children}
    </TeamsAuthContext.Provider>
  );
}

/**
 * Hook to access Teams auth context.
 */
export function useTeamsAuth(): TeamsAuthContextType {
  const context = useContext(TeamsAuthContext);
  if (!context) {
    throw new Error("useTeamsAuth must be used within a TeamsAuthProvider");
  }
  return context;
}

export default TeamsAuthProvider;
